import React, { useEffect, useState } from "react";
import { Scale } from "lucide-react";
import { Alert, Button, Dialog, RadioGroup, Textarea } from "@/components/ui";
import type { ReviewArchiveReason } from "@/services/bedrock/dtos/Review";

const NOTE_MAX_LENGTH = 500;

// Content reasons only: hiding a review because it's negative can break FTC review-suppression rules
export function archiveReasonLabel(reason: ReviewArchiveReason): string {
  switch (reason) {
    case "spam":
      return "Spam or fake";
    case "not_about_player":
      return "Meant for someone else";
    case "abusive":
      return "Abusive or offensive";
    case "private_info":
      return "Shares private information";
    case "duplicate":
      return "Duplicate";
    case "other":
      return "Something else";
  }
}

const REASONS: ReviewArchiveReason[] = ["spam", "not_about_player", "abusive", "private_info", "duplicate", "other"];

type ArchiveReviewDialogProps = {
  open: boolean;
  playerFirstName: string | null;
  isArchiving: boolean;
  onCancel: () => void;
  onArchive: (reason: ReviewArchiveReason, note: string | null) => void;
};

export function ArchiveReviewDialog({
  open,
  playerFirstName,
  isArchiving,
  onCancel,
  onArchive,
}: ArchiveReviewDialogProps) {
  const [reason, setReason] = useState<ReviewArchiveReason | null>(null);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!open) return;
    setReason(null);
    setNote("");
  }, [open]);

  const trimmedNote = note.trim();
  const canArchive = reason != null && (reason !== "other" || trimmedNote.length > 0) && !isArchiving;

  return (
    <Dialog open={open} onOpenChange={next => !next && onCancel()} className="gap-0">
      <Dialog.Header>
        <Dialog.Title>Archive this review?</Dialog.Title>
      </Dialog.Header>
      <Dialog.Content className="flex flex-col gap-4">
        <p>
          It'll be hidden from {playerFirstName ? `${playerFirstName}'s` : "the player's"} app, your review widgets and
          review pages, and it won't count toward review stats. Posts already shared from it stay up. You can restore it
          anytime from the Archived filter.
        </p>
        {/* A note, not an alert: it's there from the start, so screen readers shouldn't announce it */}
        <Alert variant="warning" role="note">
          <Scale />
          <Alert.Description>
            Archive reviews for what they say, not for their rating. Hiding a review only because it's negative can
            violate FTC rules on review suppression.
          </Alert.Description>
        </Alert>
        <div className="flex flex-col gap-2">
          <span id="archive-reason-label" className="text-sm font-semibold text-slate-700">
            Why are you archiving it?
          </span>
          <RadioGroup
            aria-labelledby="archive-reason-label"
            className="sm:grid-cols-2 gap-x-6"
            value={reason ?? ""}
            onValueChange={value => setReason(value as ReviewArchiveReason)}
          >
            {REASONS.map(value => (
              <RadioGroup.Button key={value} value={value} label={archiveReasonLabel(value)} />
            ))}
          </RadioGroup>
          {reason === "other" ? (
            <Textarea
              aria-label="Note"
              placeholder="Add a short note for your team"
              value={note}
              maxLength={NOTE_MAX_LENGTH}
              onChange={event => setNote(event.target.value)}
              autoFocus
            />
          ) : null}
        </div>
      </Dialog.Content>
      <Dialog.Footer>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="destructive"
          disabled={!canArchive}
          onClick={() => reason && onArchive(reason, reason === "other" ? trimmedNote : null)}
        >
          {isArchiving ? "Archiving…" : "Archive"}
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
}
